/**
 * 4.18 Sheet: bottom sheet with grabber, scrim, carved title line. Focus trapped; Escape, scrim tap and drag-down close.
 * On a phone on its side (D-123) it opens from the right at full height instead (`sheet--side`, no drag).
 */
import { useEffect, useRef, useState, type PointerEvent, type ReactElement, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { Hairline } from "./Section";
import { Icon } from "./Icon";
import { usePhoneLandscape } from "../lib/useDesktop";

export interface SheetProps { open: boolean; onClose: () => void; title?: string; sub?: ReactNode; children: ReactNode; cancelLabel?: string | null; className?: string; labelledBy?: string; /** A text action beside the title (D-091). */ action?: ReactNode }
const FOCUSABLE = 'a[href], button:not([aria-disabled="true"]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])';

export function Sheet({ open, onClose, title, sub, children, cancelLabel = null, className = "", action }: SheetProps): ReactElement | null {
  const ref = useRef<HTMLDivElement>(null);
  const [drag, setDrag] = useState<{ start: number; dy: number } | null>(null);
  const side = usePhoneLandscape();
  useFocusTrap(ref, open, onClose);
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow; document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);
  if (!open) return null;

  const onDown = (e: PointerEvent<HTMLDivElement>) => { if (side) return; setDrag({ start: e.clientY, dy: 0 }); (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId); };
  const onMove = (e: PointerEvent<HTMLDivElement>) => { if (drag) setDrag({ start: drag.start, dy: Math.max(0, e.clientY - drag.start) }); };
  const onUp = () => { if (drag && drag.dy > 90) onClose(); setDrag(null); };

  return createPortal(
    <>
      <div className="scrim" onClick={onClose} data-testid="scrim" />
      <div ref={ref} className={`sheet ${side ? "sheet--side" : ""} ${drag ? "sheet--dragging" : ""} ${className}`} role="dialog" aria-modal="true" aria-label={title} tabIndex={-1} style={drag ? { transform: `translate(-50%, ${drag.dy}px)` } : undefined}>
        <div onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerCancel={onUp}>
          <div className="sheet__grabber" aria-hidden="true" />
          {/* No grabber to drag on the side panel, so it gets a close button. */}
          {side && <button type="button" className="sheet__close" aria-label="Close" onClick={onClose} onPointerDown={(e) => e.stopPropagation()} data-testid="sheet-close"><Icon name="x" size={20} /></button>}
          {title && (
            <div className="sheet__head">
              {action ? <div className="sheet__title-row"><h2 className="sheet__title">{title}</h2>{/* Taps on the action must not start a drag: the drag handle captures the pointer, and the click would never land. */}<span onPointerDown={(e) => e.stopPropagation()}>{action}</span></div> : <h2 className="sheet__title">{title}</h2>}
              {/* A plain rule: the cut-terminal block at its right end read as a stray mark under a sheet title. */}
              <Hairline terminal={false} />
              {sub && <p className="sheet__sub">{sub}</p>}
            </div>
          )}
        </div>
        <div className="sheet__body">
          {children}
          {cancelLabel && <button type="button" className="sheet__cancel" onClick={onClose}>{cancelLabel}</button>}
        </div>
      </div>
    </>,
    document.body,
  );
}

export function useFocusTrap(ref: React.RefObject<HTMLElement | null>, active: boolean, onEscape: () => void): void {
  // A fresh onEscape each render must not re-run the trap, or focus jumps back to the first control on every update.
  const escape = useRef(onEscape);
  escape.current = onEscape;
  useEffect(() => {
    if (!active) return;
    const el = ref.current; if (!el) return;
    const previous = document.activeElement as HTMLElement | null;
    const first = el.querySelector<HTMLElement>(FOCUSABLE);
    (first ?? el).focus({ preventScroll: true });
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") { e.preventDefault(); escape.current(); return; }
      if (e.key !== "Tab") return;
      const items = [...el.querySelectorAll<HTMLElement>(FOCUSABLE)].filter((n) => !n.hasAttribute("hidden") && n.getAttribute("aria-hidden") !== "true");
      if (items.length === 0) { e.preventDefault(); el.focus(); return; }
      const a = items[0]!, z = items[items.length - 1]!;
      if (e.shiftKey && (document.activeElement === a || document.activeElement === el)) { e.preventDefault(); z.focus(); }
      else if (!e.shiftKey && document.activeElement === z) { e.preventDefault(); a.focus(); }
    };
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("keydown", onKey); previous?.focus?.({ preventScroll: true }); };
  }, [ref, active]);
}
